import { ControllerHelper, MediaDownloadDto, WikiArticleReviewDownloadDto, WikiColumnDownloadDto, WikiController, WikiSectionDownloadDto } from 'collaboration-service';
import { computeColumnLabel } from './WikiNumberingHelpers';
import ContentLanguageSelect from 'components/ContentLanguageSelect';
import CopyToClipboard from 'components/CopyToClipboard/CopyToClipboard';
import SafeHTML from 'components/SafeHTML/SafeHTML';
import SwitchButton from 'components/Switch/SwitchButton';
import WikiEditTable from 'components/Wiki/WikiEditTable';
import { formatRelative } from 'date-fns';
import Editor from "Dummies/TestImpl/TestEditor";
import { AutoGrid, AutoGridColumn, Button, ButtonGroup, Flag, Icon, Image, ImgIcons, RFC_4646, Tooltip, useBelowBreakpoint } from 'imaginarity-react-ui';
import * as _ from "lodash";
import * as React from 'react';
import { useSelector } from 'react-redux';
import { Actions } from 'services/ApplicationState/Actions';
import { ApplicationState, useAppDispatch, useAppSelector } from 'services/ApplicationState/ApplicationState';
import { getLink, KeyOfType, searchHtmlAndMark, searchTableAndMark, shallowCompare } from 'services/Helpers';
import { getMediaLink, uploadMediaToStorage } from 'services/Helpers/MediaHelpers';
import { useCheckWhereIAm, usePrefixedRouteHelpers } from 'services/Helpers/RoutingHelper';
import ScrollHelper from 'services/Helpers/ScrollHelper';
import { getTranslated, getTranslatedExact, getTranslatedUserName } from 'services/Helpers/TranslationHelpers';
import { ImgI18N, useImgI18N } from 'services/ImgI18N';
import { useBelowBreakpointOrEqual, useThemePart } from 'services/useAppTheme';
import { setVideoDefaultTrackByLng } from 'views/Tube/TubeContent';
import { UploadFile } from './WikiEditArticle';
import WikiEditColumn from './WikiEditColumn';
import WikiHotSpot from './WikiHotSpot';
import { WikiSC as T } from './WikiSC';
import { wikiTheme } from './WikiTheme';



const elementResizeDetectorMaker = require("element-resize-detector");
const erdUltraFast = elementResizeDetectorMaker({
    strategy: "scroll" //<- For ultra performance.
});

const mapper = (state: ApplicationState) => ({
    search: state.wikiState.search,
    contentLanguage: state.contentLanguage,
    masterLayoutRootExtension: state.masterLayoutRootExtension,
    routeParams: state.params,
    config: state.wikiState.config,
    referenceLanguage: state.wikiState.referenceLanguage
});

export interface WikiArticleSectionProps {
    section: WikiSectionDownloadDto;
    scrollHelper?: ScrollHelper;
    onIds?: (ids?: string[]) => void;
    addRefs: (name: string, ids?: string[], threshold?: number | number[]) => (r: HTMLDivElement | null) => void;
    reviewMode?: boolean;
    setEditMode?: (editMode: boolean) => void;
    isInEditMode?: boolean;
    numberingEnabled: boolean;
    numberingLevels?: (string[] | string)[];
    index: number;
    reviews?: WikiArticleReviewDownloadDto[];
    onNewReview?: (review: WikiArticleReviewDownloadDto) => Promise<void>;
    availableLngs?: string[];
}

export interface WikiArticleSectionState {
    open: boolean;
    width: number;
}

const getStartTag = (id: string) => {
    return `<b id="#searchResult${id}-{{pos}}" class="searchFinding">` +
        `<b  style="position: absolute; top: -60px; left: 0px" id="#searchResultAnchor${id}-{{pos}}"></b>`;
};

/** The reference can only be one of these - the first one is the fallback for an unset reference. */
const referenceLanguageOptions: RFC_4646[] = ["de-DE", "en-GB"];

const referenceVersionOptions: { current: boolean, label: string, icon: ImgIcons, tooltip: string }[] = [
    { current: true, label: "PAG", icon: "calendar", tooltip: "display the current version as reference from Group Garantie Conception" },
    { current: false, label: "Market", icon: "history back", tooltip: "display last year's version as reference from the published Market Group" },
];

const fromClipboardLink = <T.PulsateIcon>
    <Icon name="chevron right double" style={{ float: "left", marginRight: 5, marginLeft: -3 }} marginTop={5} />
</T.PulsateIcon>;


const WikiArticleSection = (p: WikiArticleSectionProps) => {
    const sectionRef = React.useRef<HTMLDivElement | null>(null);

    const [open, setOpen] = React.useState<boolean>(true);
    const [activeColumnIds, setActiveColumnIds] = React.useState<string[]>([]);
    const { section: s, scrollHelper, onIds, addRefs, reviewMode,
        setEditMode, isInEditMode, numberingEnabled, numberingLevels, index,
        reviews, onNewReview, availableLngs } = p;
    const { contentLanguage, routeParams, search, config } = useSelector(mapper, shallowCompare);
    const { t, currentNamespace } = useImgI18N(config.namespace ?? "wiki");

    const { createNamedRoute } = usePrefixedRouteHelpers("wiki", config.baseRoute);

    const secTitle = getTranslated(s.title, contentLanguage).text;
    const minimize = wikiTheme.articleTheme.minimizeIcon ?? "minimize";
    const maximize = wikiTheme.articleTheme.maximizeIcon ?? "maximize";
    const articleId = routeParams?.id as string;
    const routeSecId = routeParams?.secId as string;
    const routeColId = routeParams?.colId as string;


    React.useEffect(() => {
        if (reviewMode === true)
            setOpen(true);
    }, [reviewMode]);

    React.useEffect(() => {
        if (search === undefined && onIds)
            onIds(undefined);
    }, [search, onIds]);

    const onFindings = React.useMemo(() => (id: string, ids: string[]) => {
        if (ids.length !== 0) {
            if (onIds)
                onIds(ids);
        }
    }, [onIds]);





    React.useEffect(() => {
        if (activeColumnIds.length > 0 && setEditMode)
            setEditMode(true);
        else if (setEditMode)
            setEditMode(false);
    }, [activeColumnIds, setEditMode]);

    const isSmallDevice = useBelowBreakpoint("desktop");
    const sectionId = s.id;
    const urlSec = articleId ? window.location.origin + "/#" + createNamedRoute("wiki_article", { id: articleId, secId: sectionId }) : undefined;
    const isLinkedSec = routeSecId === sectionId && (routeColId === undefined || routeColId === "");

    const check = useCheckWhereIAm();

    // FALSCH!!
    const isFaq = check("qanda") || check("q&a_search");

    React.useEffect(() => {
        _.forEach(s.columns, c => {
            let table = getTranslated(c.tableContent, contentLanguage);
            let text = getTranslated(c.textContent, contentLanguage).text;
            if (search) {
                const starttag = getStartTag(c.id);
                const endtag = `</b>`;

                let findings = 0;
                const { count } = searchHtmlAndMark(text, search, starttag, endtag);
                findings += count;

                if (c.columnType === "Table") {
                    const { currentCount } = searchTableAndMark(table, search, starttag, endtag);
                    findings += currentCount;
                }
                onFindings(c.id, _.times(findings, x => `${c.id}-${x}`));
            }
            else
                onFindings(c.id, []);
        });
    }, [s, contentLanguage, search, onFindings]);

    // const { visible: sectionOnScreen, setRef: setSectionRef } = useOnScreen(0.1);
    // React.useEffect(() => {
    //     elementOnScreen(sectionOnScreen, sectionId);
    // }, [sectionOnScreen, sectionId, elementOnScreen]);


    const label = numberingEnabled ? numberingLevels?.[0]?.[index] : undefined;

    return (
        <div ref={(el) => { sectionRef.current = el; addRefs("section", [sectionId], 0.1)(el); }}>
            {isFaq ?
                <T.SectionTitle
                    isLinkedSec={false}
                    open={true}
                    noTitle={!secTitle || secTitle === ""}
                    style={{ marginBottom: 10, fontSize: "1.1em" }}
                >
                    {secTitle}
                </T.SectionTitle>
                :
                <T.SectionTitle
                    isLinkedSec={isLinkedSec ?? false}
                    open={open}
                    lang={contentLanguage}
                    // noTitle={!secTitle || secTitle === ""}
                    noTitle={!secTitle || secTitle === ""}
                >
                    {!reviewMode &&
                        <Button
                            kind="transparentButton"
                            icon={open ? minimize : maximize}
                            onClick={() => setOpen(!open)}
                            floated="right"
                        />
                    }
                    {!open &&
                        <Icon name="eye close" color="rgba(0,0,0,0.3)" size="20px" style={{ float: "left", marginRight: 10, marginTop: 5, marginLeft: 10 }} />
                    }
                    {!secTitle || secTitle === "" ? <span>&nbsp;</span> :
                        <>
                            {isLinkedSec && fromClipboardLink}
                            {label &&
                                <T.ColumnTitleLabel large={true} revMode={reviewMode}>
                                    {label}
                                </T.ColumnTitleLabel>
                            }
                            {secTitle}
                            {urlSec && open && !reviewMode &&
                                <CopyToClipboard
                                    type='icon'
                                    text={urlSec}
                                    copyHint={t("copy link to clipboard")}
                                    copiedHint={t("link copied to clipboard")}
                                />
                            }
                        </>
                    }

                </T.SectionTitle>
            }



            {open &&
                <>
                    {(s.mode === "Grid" || s.mode === "Swipe") &&
                        <AutoGrid
                            columns={isSmallDevice ? 1 : s.columnCount}
                            gap={10}
                        >
                            {_.map(s.columns, (c, i) => {
                                const colSpan = reviewMode && activeColumnIds.includes(c.id) ? s.columnCount : c.columnWidth;
                                return (
                                    <WikiArticleSectionColumn
                                        index={i}
                                        span={colSpan}
                                        key={c.id}
                                        column={c}
                                        reviewMode={reviewMode}
                                        activeColumnIds={activeColumnIds}
                                        setActiveColumnIds={setActiveColumnIds}
                                        onNewReview={onNewReview}
                                        reviews={reviews}
                                        namespace={currentNamespace}
                                        numberingEnabled={numberingEnabled}
                                        numberingLevels={numberingLevels}
                                        parentLabel={label}
                                        section={s}
                                        isInEditMode={isInEditMode}
                                        addRefs={addRefs}
                                        scrollHelper={scrollHelper}
                                        availableLngs={availableLngs ?? []}
                                    />
                                );
                            }
                            )}
                        </AutoGrid>
                    }
                </>
            }
        </div >
    );
}

type WikiArticleSectionColumnProps = {
    index: number;
    span: number;
    column: WikiColumnDownloadDto;
    reviewMode: WikiArticleSectionProps["reviewMode"];
    activeColumnIds: string[];
    setActiveColumnIds: React.Dispatch<React.SetStateAction<string[]>>;
    onNewReview: WikiArticleSectionProps["onNewReview"];
    reviews: WikiArticleSectionProps["reviews"];
    namespace: string;
    numberingEnabled: WikiArticleSectionProps["numberingEnabled"];
    numberingLevels?: WikiArticleSectionProps["numberingLevels"];
    parentLabel?: string;
    section: WikiArticleSectionProps["section"];
    isInEditMode: WikiArticleSectionProps["isInEditMode"];
    addRefs: WikiArticleSectionProps["addRefs"];
    scrollHelper: WikiArticleSectionProps["scrollHelper"];
    availableLngs?: string[];
}
export const WikiArticleSectionColumn = (p: WikiArticleSectionColumnProps) => {
    const { contentLanguage, config, search, routeParams, referenceLanguage } = useAppSelector(mapper, shallowCompare);
    const { column, reviewMode, activeColumnIds, setActiveColumnIds, onNewReview,
        reviews, namespace, numberingEnabled, numberingLevels, parentLabel, span, index, section: s, isInEditMode,
        addRefs, scrollHelper, availableLngs } = p;
    const dispatch = useAppDispatch();
    const [, setLoading] = React.useState<boolean>(false);
    const [showRefLang, setShowRefLang] = React.useState<boolean>(false);
    const [showCurrentReference, setShowCurrentReference] = React.useState<boolean>(true);
    const { createT, t, I18N, currentLanguage } = useImgI18N(namespace);
    const colLabel = numberingEnabled ? numberingLevels?.[1] : [];
    const { createNamedRoute } = usePrefixedRouteHelpers("wiki", config.baseRoute);
    const [width, setWidth] = React.useState<number>(0);
    const vidRef = React.useRef<HTMLVideoElement | null>(null);
    const t_for = createT("general");

    const rs = vidRef.current?.readyState ?? 0;
    const lng = contentLanguage ?? currentLanguage;

    const articleId = routeParams?.id as string;
    const routeSecId = routeParams?.secId as string;
    const sectionId = s.id;

    const review = React.useMemo(() => {
        const currentLng = contentLanguage ?? ImgI18N.getInstance().currentLanguage;
        const candidates = _.filter(reviews, r => r.partId === column.id && r.reviewLanguage === currentLng);
        const foundReview = _.find(candidates, r => r.id !== undefined) ?? _.first(candidates);
        if (foundReview)
            return foundReview;
        return {
            columnChanges: _.cloneDeep(column),
            partId: column.id,
            articleId: articleId ?? "",
            reviewLanguage: currentLng,
        } as WikiArticleReviewDownloadDto;
    }, [reviews, column, articleId, contentLanguage]);

    const reviewRef = React.useRef(review);
    reviewRef.current = review;
    const onNewReviewRef = React.useRef(onNewReview);
    onNewReviewRef.current = onNewReview;

    React.useEffect(() => {
        if (reviewRef.current.id === undefined) {
            onNewReviewRef.current?.(reviewRef.current);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [column.id, contentLanguage]);

    React.useEffect(() => {
        if (rs === 4)
            setVideoDefaultTrackByLng(vidRef.current, lng);
    }, [vidRef, rs, lng]);


    const windowWith = window.innerWidth;
    const isTablet = useBelowBreakpoint("tablet");
    const scaledWindowWidth = windowWith > 1920 ? 1920 : windowWith;
    const calcedContentWidth = scaledWindowWidth - (isTablet ? 0 : 360);


    const isMobile = useBelowBreakpointOrEqual("mobile");
    const { contentWidth, missingAudioSvg, missingImageSvg, emptyVideoThumbnail, emptyImageUrl, missingVideoSvg, emptyAvatarImageUrl } = useThemePart(s => ({
        contentWidth: s.content.width,
        missingAudioSvg: s.missingAudioSvg,
        missingImageSvg: s.missingImageSvg,
        missingVideoSvg: s.missingVideoSvg,
        emptyVideoThumbnail: s.emptyVideoThumbnail,
        emptyImageUrl: s.emptyImageUrl,
        emptyAvatarImageUrl: s.emptyAvatarImageUrl
    }));

    // const { reviewChangedDate, reviewChangedDummyAvatarUrl, reviewChangedUser } = React.useMemo(() => {
    //     const getDateFnsLng = ImgI18N.getInstance().languageLocal();
    //     const reviewChangedDate = formatRelative(new Date(review.changed), new Date(), { locale: getDateFnsLng });
    //     const reviewChangedUser = review.changedBy || review.creator;
    //     // const reviewChangedDummyAvatarUrl = (review.changedBy && getMediaLink(review.changedBy, u => u.avatar)) ?? emptyAvatarImageUrl;
    //     const reviewChangedDummyAvatarUrl = getMediaLink(reviewChangedUser, u => u.avatar) ?? emptyAvatarImageUrl;
    //     return {
    //         reviewChangedDate, reviewChangedDummyAvatarUrl, reviewChangedUser
    //     }
    // }, [user, emptyAvatarImageUrl]);

    const { reviewChangedDate, reviewChangedDummyAvatarUrl, reviewChangedUser } = React.useMemo(() => {
        const getDateFnsLng = ImgI18N.getInstance().languageLocal();
        let reviewChangedDate = "Invalid date";
        let reviewChangedUser = review.creator;
        let reviewChangedDummyAvatarUrl = emptyAvatarImageUrl;

        if (review.changed) {
            const changedDate = new Date(review.changed);
            if (!isNaN(changedDate.getTime())) {
                reviewChangedDate = formatRelative(changedDate, new Date(), { locale: getDateFnsLng });
            }
        }

        reviewChangedUser = review.changedBy || review.creator;
        reviewChangedDummyAvatarUrl = getMediaLink(reviewChangedUser, u => u.avatar) ?? emptyAvatarImageUrl;

        return {
            reviewChangedDate,
            reviewChangedDummyAvatarUrl,
            reviewChangedUser,
        };
    }, [review, emptyAvatarImageUrl]);


    const toggleShowRefLang = React.useMemo(() => () => {
        const show = !showRefLang;
        setShowRefLang(show);
        if (show && !_.includes(referenceLanguageOptions, referenceLanguage))
            dispatch(Actions.setWikiReferenceLanguage(_.first(referenceLanguageOptions)!));
    }, [showRefLang, referenceLanguage, dispatch]);

    const { setImageModal, removeImageModal, refLanguageChanged } = React.useMemo(() => {
        const setImageModal = (modal: JSX.Element) => {
            dispatch(Actions.setMasterLayoutExtension(() => modal));
        };
        const removeImageModal = () => {
            dispatch(Actions.setMasterLayoutExtension());
        };
        const refLanguageChanged = (lng: string) => {
            dispatch(Actions.setWikiReferenceLanguage(lng));
        };
        return { setImageModal, removeImageModal, refLanguageChanged };
    }, [dispatch]);

    const { openPartReview, closePartReview } = React.useMemo(() => {
        return {
            openPartReview: (partId: string) => () => {
                setActiveColumnIds(prev => [...prev.filter(id => id !== partId), partId]);
            },
            closePartReview: (partId: string) => () => {
                setActiveColumnIds(prev => prev.filter(id => id !== partId));
                setShowRefLang(false);
            }
        }
    }, [setActiveColumnIds]);

    const setRef = React.useMemo(() => (ref?: any) => {
        const div = ref as HTMLDivElement;
        if (div) {
            erdUltraFast.listenTo({}, div, (ele: HTMLDivElement) => {
                setWidth(w => Math.floor(ele?.offsetWidth ?? 0));
            })
        }
    }, []);

    const saveReview = React.useMemo(() => (dto: WikiArticleReviewDownloadDto) => async () => {
        setLoading(true);
        const saved = dto.id ?
            await ControllerHelper.singleCall({ id: dto.id, dto }, WikiController.ChangeReviewArticle) :
            await ControllerHelper.singleCall({ dto }, WikiController.CreateReviewArticle);
        await onNewReview?.(saved);
        setLoading(false);
        closePartReview(dto.partId)();
    }, [onNewReview, closePartReview]);

    const { updateReviewColumn, toggleBoolOfReview } = React.useMemo(() => {
        const updateReviewColumn = (column: WikiColumnDownloadDto, uploadFile?: UploadFile | UploadFile[]) => {
            const file = (_.isArray(uploadFile) ? uploadFile[0] : uploadFile)?.mediaFile;
            if (file) {
                uploadMediaToStorage(file).then((mediaLink) => {
                    if (mediaLink) {
                        column.mediaContent = _.filter((column.mediaContent ?? []), (m: MediaDownloadDto) => m.lng !== review.reviewLanguage);
                        column.mediaContent.push(
                            {
                                mediaType: file.type,
                                changed: true,
                                fileName: file.name,
                                size: file.size,
                                links: [],
                                lng: review.reviewLanguage,
                                hasStorageThumbnails: false,
                                container: mediaLink.container,
                                reference: mediaLink.reference,
                            } as any as MediaDownloadDto);
                    }
                    const updatedReview: WikiArticleReviewDownloadDto = {
                        ...review,
                        columnChanges: column,
                    };
                    onNewReview?.(updatedReview);

                })
            }
            else {
                const updatedReview: WikiArticleReviewDownloadDto = {
                    ...review,
                    columnChanges: column,
                };
                onNewReview?.(updatedReview);
            }
        };

        const toggleBoolOfReview = (what: KeyOfType<WikiArticleReviewDownloadDto, boolean>) => () => {
            const updatedReview: WikiArticleReviewDownloadDto = {
                ...review,
                [what]: !(review[what] ?? false),
            };
            onNewReview?.(updatedReview);
        };



        return { updateReviewColumn, toggleBoolOfReview };
    }, [review, onNewReview]);



    const colTitle = getTranslated(column.title, contentLanguage).text;
    const colId = column.id;
    const spanValue = span;
    const hasColTitle = !!(colTitle && colTitle !== "");
    const titledColsBefore = s.columns.slice(0, index).filter(col => {
        const t = getTranslated(col.title, contentLanguage).text;
        return t && t !== "";
    }).length;
    const label = computeColumnLabel(numberingEnabled, hasColTitle, colLabel, titledColsBefore, parentLabel);

    const media = getTranslated(column.mediaContent, contentLanguage);
    const mediaNotAvailable = !getTranslatedExact(column.mediaContent, contentLanguage) && !!(column.mediaContent?.length) && !!reviewMode;

    const isOverlayImage = column.mediaOverlay.length > 0;
    const overlayData = getTranslatedExact(column.mediaOverlay, contentLanguage);

    let table = getTranslated(column.tableContent, contentLanguage);
    const htmlNotAvailable = !getTranslatedExact(column.textContent, contentLanguage) && !!(column.textContent?.length) && !!reviewMode;
    const tableNotAvailable = !getTranslatedExact(column.tableContent, contentLanguage) && !!(column.tableContent?.length) && !!reviewMode;
    const shortLang = (contentLanguage ?? "").substring(0, 2);
    let text = getTranslated(column.textContent, contentLanguage).text;
    if (search) {
        const starttag = getStartTag(column.id);
        const endtag = `</b>`;

        const { html } = searchHtmlAndMark(text, search, starttag, endtag);
        text = html;

        if (column.columnType === "Table") {
            const { tableNew } = searchTableAndMark(table, search, starttag, endtag);
            table = tableNew;
        }
    }

    let imgWidth = media?.width;
    let imgHeight = media?.height;

    if (!imgWidth || !imgHeight) {
        imgWidth = 400;
        imgHeight = 280;
    }
    const aspect = imgWidth / imgHeight;
    const uri = getLink(media?.links, "self")?.uri;
    const urlCol = articleId ? window.location.origin + "/#" + createNamedRoute("wiki_article", { id: articleId, secId: colId }) as string : "";
    const isLinkedCol = routeSecId === colId;
    const w = media.mediaType === "image/svg+xml" ? undefined : (Math.min(width, media?.width ?? width));


    const activateRevisionMode = reviewMode;
    const hidden = !reviewMode ? false : (isInEditMode ? (activeColumnIds.includes(column.id) ? false : true) : false);
    if (hidden) {
        return null;
    }

    const remarkText = getTranslated(review.remark ?? [], review.reviewLanguage).text ?? "";

    const setRemarkText = (text: string) => {
        const updatedReview: WikiArticleReviewDownloadDto = {
            ...review,
            remark: [..._.filter(review.remark, r => r.lng !== review.reviewLanguage), { lng: review.reviewLanguage, text }],
        };
        onNewReview?.(updatedReview);
    };

    const reviewIsSaveable = review.reasonConfirmed
        ? true
        : (review.reasonMarketChanged || review.reasonTranslationChanged)
            ? !(remarkText === "" || remarkText === "<p><br></p>")
            : false;

    const reasonIsSet = review.reasonMarketChanged || review.reasonTranslationChanged || review.reasonConfirmed;
    const isVerified = review.id !== undefined && review.reviewLanguage === contentLanguage;
    //const isApproved = review.approved;

    const isAutoTranslated = column.textContent?.some(tc => tc.autoTranslation === true && tc.lng === contentLanguage) ?? false;



    return (
        <AutoGridColumn
            span={spanValue}
            key={index}
            zIndex={0}
            divRef={addRefs("column", [sectionId, column.id], 0.1)}
        >
            <T.RevisionContainer
                revisionMode={activateRevisionMode ?? false}
                noHover={activeColumnIds.includes(column.id)}
                reviewed={isVerified}
                style={{ display: hidden ? "none" : "grid" }}
            >

                <div style={{ position: "relative", width: 0, height: 0 }}>
                    <div style={{ position: "absolute", top: -60, left: 0 }} ref={scrollHelper?.getRef(column.id)} />
                </div>
                {!activeColumnIds.includes(column.id) &&
                    <>
                        <T.ColumnTitle
                            isLinkedCol={isLinkedCol ?? false}
                            style={{ lineHeight: (colTitle && colTitle !== "" && urlCol) ? "40px" : "1.6rem" }}
                        >
                            {isLinkedCol && fromClipboardLink}

                            {colTitle && colTitle !== "" && label &&
                                <T.ColumnTitleLabel>
                                    {label}
                                </T.ColumnTitleLabel>
                            }
                            {colTitle}
                            {!reviewMode && (colTitle && colTitle !== "" && urlCol) &&
                                <CopyToClipboard
                                    text={urlCol}
                                    type='icon'
                                    copyHint={t("copy link to clipboard")}
                                    copiedHint={t("link copied to clipboard")}
                                />
                            }

                            {isAutoTranslated &&
                                <T.AIHint>
                                    {t("ai translated")}
                                </T.AIHint>
                            }
                        </T.ColumnTitle>

                        {column.columnType === "Text" &&
                            <T.ColumnContent lang={contentLanguage} info={htmlNotAvailable}>
                                {htmlNotAvailable ?
                                    <div style={{ padding: 10 }}>
                                        <Icon name="exclamation circle" color="@accentRed" size={24} marginTop={-2} style={{ float: "right" }} />
                                        <SafeHTML html={t('there is no translated version available for this <b>html</b> in <b>{{lng}}</b>', { lng: t_for(shortLang) })} />
                                    </div>
                                    :
                                    <SafeHTML html={text} />
                                }
                            </T.ColumnContent>
                        }
                        {column.columnType === "Table" &&
                            <T.ColumnContent lang={contentLanguage} info={tableNotAvailable} style={{ maxWidth: (contentWidth / (isMobile ? 1 : column.columnWidth > 1 ? 1 : s.columnCount) - (isMobile ? 40 : 10)) }}>
                                {tableNotAvailable ?
                                    <div style={{ padding: 10 }}>
                                        <Icon name="exclamation circle" color="@accentRed" size={24} marginTop={-2} style={{ float: "right" }} />
                                        <SafeHTML html={t('there is no translated version available for this <b>table</b> in <b>{{lng}}</b>', { lng: t_for(shortLang) })} />
                                    </div>
                                    : <WikiEditTable
                                        noEdit
                                        table={table}
                                        onChange={() => { }}
                                    />
                                }
                            </T.ColumnContent>
                        }
                        {column.columnType === "Image" &&
                            <T.ColumnContentImage imgWidth={media?.width} ref={setRef} lang={contentLanguage} info={mediaNotAvailable}>
                                {mediaNotAvailable ?
                                    <div style={{ padding: 10 }}>
                                        <Icon name="exclamation circle" color="@accentRed" size={24} marginTop={-2} style={{ float: "right" }} />
                                        <SafeHTML html={t('there is no translated version available for this <b>image</b> in <b>{{lng}}</b>', { lng: t_for(shortLang) })} />
                                    </div>
                                    : isOverlayImage && overlayData ?
                                        <T.HotSpotContainer
                                            aspect={aspect}
                                            width={w}
                                        >
                                            <T.HotSpotContainerImage >
                                                <Image src={uri ?? emptyImageUrl} mediaType={media.mediaType} width={media.mediaType === "image/svg+xml" ? "100%" : undefined} />
                                            </T.HotSpotContainerImage>
                                            {_.map(overlayData.elements, (e, i) => {
                                                const delay = (i + 1) * 0.5;
                                                return (
                                                    <WikiHotSpot
                                                        key={i}
                                                        element={e}
                                                        mediaOverlay={column.mediaOverlay}
                                                        index={i}
                                                        delay={delay}
                                                        imgWidth={imgWidth ?? 400}
                                                        imgHeight={imgHeight ?? 280}
                                                        width={width}
                                                        offsetY={0}
                                                    />
                                                );
                                            })}
                                        </T.HotSpotContainer>
                                        :
                                        <Image
                                            src={uri ?? missingImageSvg}
                                            onClickReturnModal={setImageModal}
                                            onRemoveModal={removeImageModal}
                                            mediaType={media.mediaType}
                                            maxModalWidth={calcedContentWidth + "px"}
                                        />
                                }
                            </T.ColumnContentImage>
                        }
                        {column.columnType === "Video" &&
                            <T.ColumnContent lang={contentLanguage} info={mediaNotAvailable}>
                                {mediaNotAvailable ?
                                    <div style={{ padding: 10 }}>
                                        <Icon name="exclamation circle" color="@accentRed" size={24} marginTop={-2} style={{ float: "right" }} />
                                        <SafeHTML html={t('there is no translated version available for this <b>video</b> in <b>{{lng}}</b>', { lng: t_for(shortLang) })} />
                                    </div>
                                    : uri ?
                                        <video
                                            poster={getMediaLink(column, y => _.first(getTranslated(y.mediaContent, contentLanguage).thumbnails)) ?? emptyVideoThumbnail}
                                            src={uri ?? ""}
                                            controls preload="auto"
                                            width="100%"
                                            style={{ outline: "none" }}
                                            crossOrigin="anonymous"
                                            ref={vidRef}
                                        >
                                            {_.map(media.subtitles, (s) => {
                                                const l = _.find(s.links, l => l.ref === "self");
                                                const label = t(`tube::${s.lng}`)
                                                if (l && label) {
                                                    return (
                                                        <track key={s.reference} id={s.lng} src={l?.uri} label={label} kind="captions" srcLang={s.lng} default={false} />
                                                    );
                                                }
                                                return null;
                                            })}
                                        </video>
                                        :
                                        <Image src={missingVideoSvg} mediaType="svg+xml" />
                                }
                            </T.ColumnContent>
                        }
                        {column.columnType === "Audio" &&
                            <T.ColumnContent lang={contentLanguage} info={mediaNotAvailable}>
                                <T.AudioContainerHeader>
                                    <Icon name="audio" style={{ float: "left", marginRight: 10 }} />{t("audiofile")}
                                </T.AudioContainerHeader>
                                {mediaNotAvailable ?
                                    <div style={{ padding: 10 }}>
                                        <Icon name="exclamation circle" color="@accentRed" size={24} marginTop={-2} style={{ float: "right" }} />
                                        <SafeHTML html={t('there is no translated version available for this <b>audio</b> in <b>{{lng}}</b>', { lng: t_for(shortLang) })} />
                                    </div>
                                    : uri ?
                                        <audio
                                            src={uri ?? ""}
                                            controls
                                            crossOrigin="anonymous"
                                            controlsList="nodownload"
                                            style={{ outline: "none", width: "100%", height: 40, background: "#F1F3F4" }}
                                        />
                                        :
                                        <Image src={missingAudioSvg} mediaType="svg+xml" />
                                }
                            </T.ColumnContent>
                        }
                    </>
                }

                {activateRevisionMode &&
                    <T.RevisionFooter className='revFoot'>
                        {reviewMode && !activeColumnIds.includes(column.id) ?
                            <>
                                {isVerified &&
                                    <div style={{
                                        float: "left",
                                        marginLeft: 0,
                                        height: 36,
                                        width: 36,
                                        cursor: "pointer",
                                        padding: "5px 0px 0 5px"
                                    }}
                                    >
                                        <Image
                                            src={reviewChangedDummyAvatarUrl}
                                            rounded
                                            width="30px"
                                            squared
                                            shadowed
                                        />
                                    </div>
                                }
                                {isVerified ?
                                    <div style={{ display: "grid", gridTemplateColumns: "max-content max-content", columnGap: 5, float: "left", marginLeft: 10, lineHeight: "15px", marginTop: 5, fontSize: "13px" }}>
                                        <div style={{ fontWeight: "bolder" }}>{t("reviewed by")}:</div>
                                        <div>{getTranslatedUserName(reviewChangedUser)} <span style={{ marginLeft: 10 }}>({reviewChangedUser?.email})</span></div>
                                        <div style={{ fontWeight: "bolder" }}>{t("review Date")}:</div>
                                        <div>{reviewChangedDate}</div>
                                    </div>
                                    :
                                    <div style={{ float: "left", marginLeft: 10, lineHeight: "40px", fontSize: "13px" }}>
                                        <Tooltip tooltipText={t("this part has not yet been verified")} noMargin position='top' >
                                            <div>
                                                <Icon name="eye close" style={{ float: "left", marginRight: 5 }} marginTop={6} />
                                                {t("not yet verified")}
                                            </div>
                                        </Tooltip>
                                    </div>
                                }
                                <Button
                                    kind="halfTransparentButton"
                                    floated='right'
                                    icon='edit'
                                    content={t("review")}
                                    tooltip={{ tooltipText: t("review this part"), background: "@accentLightBlue", fontColor: "@mainBackground", position: "top" }}
                                    onClick={openPartReview(column.id)}
                                />
                                <div style={{ width: 150, float: "right" }}>
                                    <ContentLanguageSelect languages={availableLngs} />
                                </div>
                            </>
                            :

                            <Button
                                kind="primary"
                                floated='right'
                                icon='send'
                                content={t("send")}
                                disabled={!reviewIsSaveable}
                                tooltip={{ tooltipText: t("send the review for verification"), background: "@accentRed", fontColor: "@mainBackground", position: "top" }}
                                onClick={saveReview(review)}
                            />
                        }
                        {reviewMode && activeColumnIds.includes(column.id) && (
                            <Button
                                kind="secondary"
                                floated='left'
                                icon='times'
                                content={t("close")}
                                onClick={closePartReview(column.id)}
                            />
                        )}
                    </T.RevisionFooter>
                }
                {reviewMode && activeColumnIds.includes(column.id) && (
                    <T.ActiveEditRevisionContainer>
                        <T.ActiveEditRevisionTitle>
                            <I18N text="show reference language" namespace='warranty' />
                        </T.ActiveEditRevisionTitle>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr max-content max-content max-content max-content", alignItems: "center", columnGap: 10 }}>
                            <SwitchButton isActive={toggleShowRefLang} floated='left' activeColor='@accentLightBlue' initialState={showRefLang} />
                            {showRefLang ?
                                <>
                                    <div style={{ paddingLeft: 30 }}>Reference Language:</div>
                                    <div style={{ paddingLeft: 0 }}>
                                        <ButtonGroup width={200}>
                                            {_.map(referenceLanguageOptions, lng => {
                                                const label = t_for(lng.substring(0, 2));
                                                const active = referenceLanguage === lng;
                                                return (
                                                    <Button
                                                        key={lng}
                                                        cursor={active ? "default" : "pointer"}
                                                        onClick={active ? undefined : () => refLanguageChanged(lng)}
                                                        kind={active ? "editButton" : "halfTransparentButton"}
                                                        content={<T.RefLngButtonContent><Flag name={lng} size={16} />{label}</T.RefLngButtonContent>}
                                                        tooltip={active ? undefined : { tooltipText: t("show {{lng}} as reference language", { lng: label }) }}
                                                        fluid={true}
                                                    />
                                                );
                                            })}
                                        </ButtonGroup>
                                    </div>
                                    <div style={{ paddingLeft: 50 }}>Current Version</div>
                                    <ButtonGroup width={200}>
                                        {_.map(referenceVersionOptions, o => {
                                            const active = showCurrentReference === o.current;
                                            return (
                                                <Button
                                                    key={o.label}
                                                    cursor={active ? "default" : "pointer"}
                                                    onClick={active ? undefined : () => setShowCurrentReference(o.current)}
                                                    kind={active ? "editButton" : "halfTransparentButton"}
                                                    content={o.label}
                                                    icon={o.icon}
                                                    tooltip={active ? undefined : { tooltipText: t(o.tooltip) }}
                                                    fluid={true}
                                                />
                                            );
                                        })}
                                    </ButtonGroup>
                                </>
                                : <div />
                            }
                        </div>


                        <T.ActiveEditRevisionTitle>
                            <I18N text="review language" namespace='warranty' />
                        </T.ActiveEditRevisionTitle>
                        <ContentLanguageSelect languages={availableLngs} />

                        <T.ActiveEditRevisionTitle style={{ marginBottom: -3 }}>
                            <I18N text="{{columntype}} content" data={{ columntype: column.columnType }} namespace='warranty' />
                            <Button icon="info" kind="fullTransparentButton" floated='right' tooltip={{
                                tooltipText: "",
                                tooltipContent: <div style={{ textAlign: "left" }}><SafeHTML html={t('editing is only possible if <b>market change</b> or <b>translation edit</b> has been selected as review reason')} /></div>
                            }} />
                        </T.ActiveEditRevisionTitle>
                        <div style={{ marginBottom: -3 }}>
                            <WikiEditColumn
                                column={review!.columnChanges!}
                                columnCount={column.columnWidth}
                                columnChanged={updateReviewColumn}
                                showReference={showRefLang}
                                reviewMode={true}
                                referenceColumn={column}
                                language={review.reviewLanguage}
                                activeEditor={(review.reasonMarketChanged === true || review.reasonTranslationChanged === true)}
                            />
                        </div>


                        <T.ActiveEditRevisionTitle error={!reasonIsSet}>
                            <I18N text="review reason" namespace='warranty' />
                            <Icon
                                name={reasonIsSet ? "check" : "asterisk"}
                                color={reasonIsSet ? "@accentGreen" : "@accentRed"}
                                marginTop={3}
                                style={{ marginLeft: 3, marginRight: 0, float: "right" }}
                            />
                        </T.ActiveEditRevisionTitle>
                        <T.SwitchReasonGrid>
                            <SwitchButton isActive={toggleBoolOfReview("reasonMarketChanged")} floated='left' activeColor='@accentRed' disabled={review.reasonConfirmed} initialState={review.reasonMarketChanged} />
                            <div>
                                <T.SwitchReasonGridIcon shown={review.reasonConfirmed}>
                                    <Icon name="lock" style={{ float: "left" }} marginTop={4} color='@accentRed' />
                                </T.SwitchReasonGridIcon>
                                <T.SwitchReasonGridText active={review.reasonMarketChanged} disabled={review.reasonConfirmed} >Market change</T.SwitchReasonGridText>
                            </div>
                            <SwitchButton isActive={toggleBoolOfReview("reasonTranslationChanged")} floated='left' activeColor='@accentRed' disabled={review.reasonConfirmed} initialState={review.reasonTranslationChanged} />
                            <div>
                                <T.SwitchReasonGridIcon shown={review.reasonConfirmed} >
                                    <Icon name="lock" style={{ float: "left" }} marginTop={4} color='@accentRed' />
                                </T.SwitchReasonGridIcon>
                                <T.SwitchReasonGridText active={review.reasonTranslationChanged} disabled={review.reasonConfirmed}>Translation edited</T.SwitchReasonGridText>
                            </div>
                            <SwitchButton isActive={toggleBoolOfReview("reasonConfirmed")} floated='left' activeColor='@accentGreen' disabled={review.reasonMarketChanged || review.reasonTranslationChanged} initialState={review.reasonConfirmed} />
                            <div>
                                <T.SwitchReasonGridIcon shown={review.reasonMarketChanged || review.reasonTranslationChanged} >
                                    <Icon name="lock" style={{ float: "left" }} marginTop={4} color='@accentRed' />
                                </T.SwitchReasonGridIcon>
                                <T.SwitchReasonGridText active={review.reasonConfirmed} disabled={review.reasonMarketChanged || review.reasonTranslationChanged} >Confirmed</T.SwitchReasonGridText>
                            </div>
                        </T.SwitchReasonGrid>

                        <T.ActiveEditRevisionTitle error={(remarkText !== "" && remarkText !== "<p><br></p>") ? false : (review.reasonMarketChanged || review.reasonTranslationChanged)}>
                            <I18N text="remark" namespace='warranty' />
                            {((review.reasonMarketChanged || review.reasonTranslationChanged)) &&
                                <Icon
                                    name={(remarkText === "" || remarkText === "<p><br></p>") ? "asterisk" : "check"}
                                    color={(remarkText === "" || remarkText === "<p><br></p>") ? "@accentRed" : "@accentGreen"}
                                    marginTop={3}
                                    style={{ marginLeft: 3, marginRight: 0, float: "right" }}
                                />
                            }
                        </T.ActiveEditRevisionTitle>
                        <div >
                            <Editor
                                value={remarkText}
                                onChange={setRemarkText}
                                showExpandButton={false}
                                showHelpButton={false}
                                showPreviewButton={false}
                                formats={[
                                    'bold',
                                    'list',
                                    'underline',
                                    'color',
                                ]}

                            />
                        </div>
                    </T.ActiveEditRevisionContainer>
                )}
            </T.RevisionContainer>
        </AutoGridColumn>

    )
}


export default WikiArticleSection;